import React, { useState, useEffect } from 'react';
import { Plus, PencilSimple, Trash, Wrench, Calendar as CalendarIcon, Receipt } from '@phosphor-icons/react';
import axios from '../../utils/axiosInstance';
import { vehicleLabel } from '../../utils/vehicleLabel';
import { useAuth } from '../../contexts/AuthContext';
import { WlasnoscFilter, WlasnoscBadge, normalizeWlasnosc } from '../shared/WlasnoscFilter';

const API = `${window.location.origin}/api`;

const EMPTY_FORM = {
  pojazd_id: '',
  data: '',
  opis_naprawy: '',
  koszt_czesci: '',
  koszt_pracy: '',
  koszt: '',
  przebieg: '',
};

export default function NaprawyTab() {
  const { user } = useAuth();
  const canEdit = user?.role === 'admin' || user?.role === 'mechanic';
  const isAdmin = canEdit;

  const [subTab, setSubTab] = useState('historia'); // 'historia' | 'rozliczenia'
  const [naprawy, setNaprawy] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [wlasnoscFilter, setWlasnoscFilter] = useState('all');

  const pojazdWlasnoscById = React.useMemo(() => {
    const m = {};
    for (const p of vehicles) m[p.id] = normalizeWlasnosc(p.wlasnosc);
    return m;
  }, [vehicles]);

  const filteredNaprawy = React.useMemo(() => {
    if (wlasnoscFilter === 'all') return naprawy;
    return naprawy.filter((n) => (pojazdWlasnoscById[n.pojazd_id] || 'firmowy') === wlasnoscFilter);
  }, [naprawy, wlasnoscFilter, pojazdWlasnoscById]);

  const wlasnoscCounts = React.useMemo(() => {
    const c = { all: naprawy.length, firmowy: 0, prywatny: 0 };
    for (const n of naprawy) c[pojazdWlasnoscById[n.pojazd_id] || 'firmowy']++;
    return c;
  }, [naprawy, pojazdWlasnoscById]);

  const [formData, setFormData] = useState(EMPTY_FORM);

  useEffect(() => {
    fetchNaprawy();
    fetchVehicles();
  }, []);

  const fetchVehicles = async () => {
    try {
      const response = await axios.get(`${API}/terminy`);
      setVehicles(response.data);
    } catch (err) {
      console.error('Error fetching vehicles:', err);
    }
  };

  const fetchNaprawy = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/naprawy`);
      setNaprawy(response.data);
    } catch (err) {
      console.error('Error fetching naprawy:', err);
      alert('Błąd podczas pobierania danych');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const czesci = parseFloat(formData.koszt_czesci) || 0;
      const pracy = parseFloat(formData.koszt_pracy) || 0;
      const kosztExplicit = parseFloat(formData.koszt);
      // If admin only filled the split fields, sum them into `koszt`; otherwise
      // use their explicit total (keeps backward compat).
      const koszt = !isNaN(kosztExplicit) && kosztExplicit > 0 ? kosztExplicit : (czesci + pracy);
      const payload = {
        pojazd_id: formData.pojazd_id,
        data: formData.data,
        opis_naprawy: formData.opis_naprawy,
        koszt,
        koszt_czesci: czesci,
        koszt_pracy: pracy,
        przebieg: parseInt(formData.przebieg) || 0,
      };

      if (editingId) {
        await axios.put(`${API}/naprawy/${editingId}`, payload);
      } else {
        await axios.post(`${API}/naprawy`, payload);
      }

      setFormData(EMPTY_FORM);
      setShowForm(false);
      setEditingId(null);
      fetchNaprawy();
    } catch (err) {
      console.error('Error saving naprawa:', err);
      const errorMsg = err.response?.data?.detail || 'Błąd podczas zapisywania';
      alert(errorMsg);
    }
  };

  const handleEdit = (naprawa) => {
    setFormData({
      pojazd_id: naprawa.pojazd_id,
      data: naprawa.data,
      opis_naprawy: naprawa.opis_naprawy,
      koszt_czesci: naprawa.koszt_czesci != null ? String(naprawa.koszt_czesci) : '',
      koszt_pracy: naprawa.koszt_pracy != null ? String(naprawa.koszt_pracy) : '',
      koszt: naprawa.koszt != null ? String(naprawa.koszt) : '',
      przebieg: naprawa.przebieg != null ? String(naprawa.przebieg) : '',
    });
    setEditingId(naprawa.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Czy na pewno chcesz usunąć ten wpis?')) return;
    try {
      await axios.delete(`${API}/naprawy/${id}`);
      fetchNaprawy();
    } catch (err) {
      console.error('Error deleting naprawa:', err);
      alert('Błąd podczas usuwania');
    }
  };

  const handleCancel = () => {
    setFormData(EMPTY_FORM);
    setShowForm(false);
    setEditingId(null);
  };

  const toggleFlag = async (naprawa, field) => {
    try {
      const next = !naprawa[field];
      // Optimistic update
      setNaprawy((prev) => prev.map((n) => (n.id === naprawa.id ? { ...n, [field]: next } : n)));
      await axios.put(`${API}/naprawy/${naprawa.id}`, { [field]: next });
    } catch (err) {
      console.error(`Error toggling ${field}:`, err);
      alert('Błąd aktualizacji');
      fetchNaprawy(); // rollback
    }
  };

  if (loading) {
    return <div className="text-center py-12">Ładowanie...</div>;
  }

  const kosztPracy = (n) => Number(n.koszt_pracy || 0);
  const pozostaloDoZaplaty = filteredNaprawy
    .filter((n) => !n.rozliczono && kosztPracy(n) > 0)
    .reduce((s, n) => s + kosztPracy(n), 0);
  const pozostaloDoFaktury = filteredNaprawy
    .filter((n) => !n.wystawiona_faktura && kosztPracy(n) > 0)
    .reduce((s, n) => s + kosztPracy(n), 0);

  return (
    <div className="max-w-[1920px] mx-auto px-6 py-6 space-y-6">
      <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
        <div>
          <h2 className="text-3xl font-bold text-slate-800">Naprawy pojazdów</h2>
          <p className="text-slate-600 mt-1">Historia napraw i rozliczenia z mechanikiem</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <WlasnoscFilter value={wlasnoscFilter} onChange={setWlasnoscFilter} counts={wlasnoscCounts} />
          {canEdit && subTab === 'historia' && (
            <button
              onClick={() => setShowForm(!showForm)}
              data-testid="naprawy-add-btn"
              className="px-5 py-3 bg-muted hover:from-orange-700 hover:to-red-800 text-white rounded-xl font-semibold flex items-center gap-2 shadow-lg hover:border border-border transition-all"
            >
              <Plus className="w-5 h-5" weight="bold" />
              Dodaj wpis
            </button>
          )}
        </div>
      </div>

      {/* Sub-tab switcher */}
      <div className="flex gap-2 border-b border-slate-200">
        <button
          onClick={() => setSubTab('historia')}
          data-testid="naprawy-subtab-historia"
          className={`px-4 py-2 text-sm font-semibold border-b-2 transition-colors ${
            subTab === 'historia' ? 'border-orange-600 text-orange-600' : 'border-transparent text-slate-500 hover:text-slate-700'
          }`}
        >
          <Wrench className="w-4 h-4 inline mr-2" /> Historia napraw
        </button>
        {isAdmin && <button
          onClick={() => setSubTab('rozliczenia')}
          data-testid="naprawy-subtab-rozliczenia"
          className={`px-4 py-2 text-sm font-semibold border-b-2 transition-colors ${
            subTab === 'rozliczenia' ? 'border-orange-600 text-orange-600' : 'border-transparent text-slate-500 hover:text-slate-700'
          }`}
        >
          <Receipt className="w-4 h-4 inline mr-2" /> Rozliczenia z mechanikiem
        </button>}
      </div>

      {/* Add/Edit Form */}
      {canEdit && subTab === 'historia' && showForm && (
        <div className="bg-muted rounded-sm p-6 shadow-lg border border-orange-200">
          <h3 className="text-xl font-bold text-slate-800 mb-4">{editingId ? 'Edytuj naprawę' : 'Dodaj nową naprawę'}</h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Pojazd *</label>
                <select
                  value={formData.pojazd_id}
                  onChange={(e) => setFormData({ ...formData, pojazd_id: e.target.value })}
                  required
                  data-testid="naprawa-pojazd-input"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                >
                  <option value="">Wybierz pojazd</option>
                  {vehicles.map((v) => (
                    <option key={v.id} value={v.id}>{vehicleLabel(v)}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Data naprawy *</label>
                <input
                  type="date"
                  value={formData.data}
                  onChange={(e) => setFormData({ ...formData, data: e.target.value })}
                  required
                  data-testid="naprawa-data-input"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-semibold text-slate-700 mb-2">Opis naprawy *</label>
                <textarea
                  value={formData.opis_naprawy}
                  onChange={(e) => setFormData({ ...formData, opis_naprawy: e.target.value })}
                  required
                  rows={3}
                  placeholder="Szczegółowy opis wykonanej naprawy..."
                  data-testid="naprawa-opis-input"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Koszt części (zł)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.koszt_czesci}
                  onChange={(e) => setFormData({ ...formData, koszt_czesci: e.target.value })}
                  placeholder="0.00"
                  data-testid="naprawa-koszt-czesci-input"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Koszt pracy — mechanik (zł)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.koszt_pracy}
                  onChange={(e) => setFormData({ ...formData, koszt_pracy: e.target.value })}
                  placeholder="0.00"
                  data-testid="naprawa-koszt-pracy-input"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Koszt całkowity (zł)
                  <span className="ml-2 text-xs font-normal text-slate-500">— jeśli puste, wyliczy się z części + pracy</span>
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.koszt}
                  onChange={(e) => setFormData({ ...formData, koszt: e.target.value })}
                  placeholder={(() => {
                    const s = (parseFloat(formData.koszt_czesci) || 0) + (parseFloat(formData.koszt_pracy) || 0);
                    return s > 0 ? s.toFixed(2) : '0.00';
                  })()}
                  data-testid="naprawa-koszt-input"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Przebieg (km) *</label>
                <input
                  type="number"
                  value={formData.przebieg}
                  onChange={(e) => setFormData({ ...formData, przebieg: e.target.value })}
                  required
                  placeholder="0"
                  data-testid="naprawa-przebieg-input"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
              </div>
            </div>
            <div className="flex gap-3 pt-2">
              <button
                type="submit"
                data-testid="naprawa-save-btn"
                className="px-6 py-3 bg-orange-600 hover:bg-orange-700 text-white rounded-lg font-semibold transition-colors"
              >
                {editingId ? 'Zapisz zmiany' : 'Dodaj naprawę'}
              </button>
              <button
                type="button"
                onClick={handleCancel}
                className="px-6 py-3 bg-gray-300 hover:bg-gray-400 text-gray-700 rounded-lg font-semibold transition-colors"
              >
                Anuluj
              </button>
            </div>
          </form>
        </div>
      )}

      {/* HISTORIA sub-tab */}
      {subTab === 'historia' && (
        <>
          <div className="bg-white rounded-sm shadow-lg border border-gray-100 overflow-hidden">
            {filteredNaprawy.length === 0 ? (
              <div className="text-center py-16">
                <Wrench className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 text-lg">
                  {naprawy.length === 0 ? 'Brak wpisów napraw' : 'Brak wpisów w wybranym filtrze'}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-muted">
                    <tr>
                      <th className="text-left py-4 px-6 text-xs font-bold uppercase tracking-wider text-slate-600">Pojazd</th>
                      <th className="text-left py-4 px-6 text-xs font-bold uppercase tracking-wider text-slate-600">Data</th>
                      <th className="text-left py-4 px-6 text-xs font-bold uppercase tracking-wider text-slate-600">Opis naprawy</th>
                      <th className="text-right py-4 px-6 text-xs font-bold uppercase tracking-wider text-slate-600">Części</th>
                      <th className="text-right py-4 px-6 text-xs font-bold uppercase tracking-wider text-slate-600">Praca</th>
                      <th className="text-right py-4 px-6 text-xs font-bold uppercase tracking-wider text-slate-600">Razem</th>
                      <th className="text-right py-4 px-6 text-xs font-bold uppercase tracking-wider text-slate-600">Przebieg</th>
                      {isAdmin && <th className="text-right py-4 px-6 text-xs font-bold uppercase tracking-wider text-slate-600">Akcje</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredNaprawy.map((naprawa, idx) => (
                      <tr key={naprawa.id} className={`hover:bg-orange-50 transition-colors ${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/40'}`}>
                        <td className="py-4 px-6 border-b border-gray-100">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-muted rounded-lg flex items-center justify-center">
                              <Wrench className="w-6 h-6 text-white" weight="bold" />
                            </div>
                            <div className="flex flex-col gap-1">
                              <span className="font-semibold text-slate-800 text-base">{vehicleLabel(naprawa, 'Unknown')}</span>
                              <WlasnoscBadge value={pojazdWlasnoscById[naprawa.pojazd_id]} size="xs" />
                            </div>
                          </div>
                        </td>
                        <td className="py-4 px-6 border-b border-gray-100">
                          <div className="flex items-center gap-2 text-slate-700">
                            <CalendarIcon className="w-5 h-5 text-orange-600" />
                            <span className="font-medium">{new Date(naprawa.data).toLocaleDateString('pl-PL')}</span>
                          </div>
                        </td>
                        <td className="py-4 px-6 border-b border-gray-100">
                          <p className="text-slate-700 text-sm line-clamp-2">{naprawa.opis_naprawy}</p>
                        </td>
                        <td className="py-4 px-6 border-b border-gray-100 text-right text-slate-700 font-mono text-sm">
                          {Number(naprawa.koszt_czesci || 0).toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} zł
                        </td>
                        <td className="py-4 px-6 border-b border-gray-100 text-right text-slate-700 font-mono text-sm">
                          {Number(naprawa.koszt_pracy || 0).toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} zł
                        </td>
                        <td className="py-4 px-6 border-b border-gray-100 text-right">
                          <span className="text-lg font-bold text-red-600">
                            {Number(naprawa.koszt || 0).toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} zł
                          </span>
                        </td>
                        <td className="py-4 px-6 border-b border-gray-100 text-right">
                          <span className="text-slate-700 font-semibold">
                            {Number(naprawa.przebieg || 0).toLocaleString('pl-PL')} km
                          </span>
                        </td>
                        {isAdmin && (
                          <td className="py-4 px-6 border-b border-gray-100 text-right">
                            <div className="flex items-center justify-end gap-2">
                              <button onClick={() => handleEdit(naprawa)} className="p-2 hover:bg-muted rounded-lg text-foreground" title="Edytuj">
                                <PencilSimple className="w-5 h-5" weight="bold" />
                              </button>
                              <button onClick={() => handleDelete(naprawa.id)} className="p-2 hover:bg-red-100 rounded-lg text-red-600" title="Usuń">
                                <Trash className="w-5 h-5" weight="bold" />
                              </button>
                            </div>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {naprawy.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-muted rounded-sm p-6 text-white shadow-lg">
                <div className="flex items-center gap-3 mb-2">
                  <Wrench className="w-8 h-8" weight="bold" />
                  <h3 className="text-lg font-semibold">Łączna liczba napraw</h3>
                </div>
                <p className="text-4xl font-bold">{naprawy.length}</p>
              </div>
              <div className="bg-signal rounded-sm p-6 text-white shadow-lg">
                <div className="flex items-center gap-3 mb-2">
                  <h3 className="text-lg font-semibold">Łączny koszt</h3>
                </div>
                <p className="text-4xl font-bold">
                  {naprawy.reduce((sum, n) => sum + Number(n.koszt || 0), 0).toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} zł
                </p>
              </div>
              <div className="bg-muted rounded-sm p-6 text-white shadow-lg">
                <div className="flex items-center gap-3 mb-2">
                  <h3 className="text-lg font-semibold">Średni koszt naprawy</h3>
                </div>
                <p className="text-4xl font-bold">
                  {(naprawy.reduce((sum, n) => sum + Number(n.koszt || 0), 0) / naprawy.length).toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} zł
                </p>
              </div>
            </div>
          )}
        </>
      )}

      {/* ROZLICZENIA sub-tab */}
      {isAdmin && subTab === 'rozliczenia' && (
        <>
          <div className="bg-white rounded-sm shadow-lg border border-gray-100 overflow-hidden" data-testid="rozliczenia-table">
            {filteredNaprawy.filter((n) => kosztPracy(n) > 0).length === 0 ? (
              <div className="text-center py-16">
                <Receipt className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 text-lg">Brak napraw z kosztem pracy mechanika</p>
                <p className="text-gray-400 text-sm mt-2">Wpisz „Koszt pracy" przy dodawaniu naprawy, aby pojawiła się tutaj do rozliczenia.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-muted">
                    <tr>
                      <th className="text-left py-4 px-6 text-xs font-bold uppercase tracking-wider text-slate-600">Data</th>
                      <th className="text-left py-4 px-6 text-xs font-bold uppercase tracking-wider text-slate-600">Pojazd</th>
                      <th className="text-left py-4 px-6 text-xs font-bold uppercase tracking-wider text-slate-600">Opis</th>
                      <th className="text-right py-4 px-6 text-xs font-bold uppercase tracking-wider text-slate-600">Koszt pracy</th>
                      <th className="text-center py-4 px-6 text-xs font-bold uppercase tracking-wider text-slate-600">Rozliczono</th>
                      <th className="text-center py-4 px-6 text-xs font-bold uppercase tracking-wider text-slate-600">Wystawiona faktura</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredNaprawy
                      .filter((n) => kosztPracy(n) > 0)
                      .sort((a, b) => (a.data || '').localeCompare(b.data || ''))
                      .map((naprawa, idx) => (
                        <tr
                          key={naprawa.id}
                          data-testid={`rozliczenie-row-${naprawa.id}`}
                          className={`hover:bg-orange-50 transition-colors ${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/40'} ${
                            naprawa.rozliczono && naprawa.wystawiona_faktura ? 'opacity-60' : ''
                          }`}
                        >
                          <td className="py-4 px-6 border-b border-gray-100 text-slate-700 whitespace-nowrap">
                            {new Date(naprawa.data).toLocaleDateString('pl-PL')}
                          </td>
                          <td className="py-4 px-6 border-b border-gray-100">
                            <div className="flex items-center gap-2">
                              <span className="font-semibold text-slate-800">{vehicleLabel(naprawa)}</span>
                              <WlasnoscBadge value={pojazdWlasnoscById[naprawa.pojazd_id]} size="xs" />
                            </div>
                          </td>
                          <td className="py-4 px-6 border-b border-gray-100 text-slate-600 text-sm max-w-md">
                            <p className="line-clamp-2">{naprawa.opis_naprawy}</p>
                          </td>
                          <td className="py-4 px-6 border-b border-gray-100 text-right">
                            <span className="text-base font-bold text-orange-700 font-mono">
                              {kosztPracy(naprawa).toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} zł
                            </span>
                          </td>
                          <td className="py-4 px-6 border-b border-gray-100 text-center">
                            <label className="inline-flex items-center justify-center cursor-pointer">
                              <input
                                type="checkbox"
                                checked={!!naprawa.rozliczono}
                                disabled={!isAdmin}
                                onChange={() => toggleFlag(naprawa, 'rozliczono')}
                                data-testid={`rozliczenie-rozliczono-${naprawa.id}`}
                                className="w-5 h-5 accent-emerald-600 cursor-pointer"
                              />
                            </label>
                          </td>
                          <td className="py-4 px-6 border-b border-gray-100 text-center">
                            <label className="inline-flex items-center justify-center cursor-pointer">
                              <input
                                type="checkbox"
                                checked={!!naprawa.wystawiona_faktura}
                                disabled={!isAdmin}
                                onChange={() => toggleFlag(naprawa, 'wystawiona_faktura')}
                                data-testid={`rozliczenie-faktura-${naprawa.id}`}
                                className="w-5 h-5 accent-sky-600 cursor-pointer"
                              />
                            </label>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Totals */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div
              className="rounded-sm p-6 shadow-lg border-2 border-orange-400 bg-orange-50"
              data-testid="rozliczenia-total-do-zaplaty"
            >
              <div className="flex items-center gap-3 mb-2">
                <Receipt className="w-7 h-7 text-orange-700" weight="bold" />
                <h3 className="text-lg font-semibold text-orange-900">Pozostało do zapłaty</h3>
              </div>
              <p className="text-4xl font-bold text-orange-800 font-mono">
                {pozostaloDoZaplaty.toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} zł
              </p>
              <p className="text-xs text-orange-700 mt-2">Suma kosztu pracy z rekordów bez zaznaczonej „Rozliczono"</p>
            </div>
            <div
              className="rounded-sm p-6 shadow-lg border-2 border-sky-400 bg-sky-50"
              data-testid="rozliczenia-total-do-faktury"
            >
              <div className="flex items-center gap-3 mb-2">
                <Receipt className="w-7 h-7 text-sky-700" weight="bold" />
                <h3 className="text-lg font-semibold text-sky-900">Pozostało do faktury</h3>
              </div>
              <p className="text-4xl font-bold text-sky-800 font-mono">
                {pozostaloDoFaktury.toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} zł
              </p>
              <p className="text-xs text-sky-700 mt-2">Suma kosztu pracy z rekordów bez zaznaczonej „Wystawiona faktura"</p>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
